import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { DatePicker } from '../components/Planning/components/DatePicker';
import { parseYMDToLocalDate, formatDateToYMD } from '../utils/dateFormat';
import api from '../api';
import { getUserInfo } from '../user';
import SearchSelect from './SearchSelect';
import HostModal from '../components/HostModal';
import { useVanoCodes } from '../context/VanoCodesContext';
import { activityLabel } from '../utils/vanoCodes';
import { parseGps } from '../utils/gps';
import { optionLabel, optionsWithCurrent } from '../utils/archive';
import TriStateSelect from '../components/TriStateSelect';
import { yearWarning } from '../utils/yearWarning';
import { TRI_STATE_FIELDS } from '../utils/triState';
import { SAMPLE_FIELDS, shownSamples, takesSamples } from '../utils/samples';
import { ArchivedBadge, ArchivedDataBanner } from '../components/masterdata/ArchiveControls';
import { TRIAL_MAX_LENGTH, NON_NEGATIVE_NUMBER, YEAR_NUMBER, FIELD_PHASE_START_MONTH, FIELD_PHASE_END_MONTH } from '../utils/trialFieldLimits';
import { CO_LABEL } from '../utils/contractLabel';

const byId = (rows, id) => rows.find((r) => String(r.id) === String(id ?? ''));

// Fix for default marker icon in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Map marker component with click event handling
function LocationMarker({ position, setPosition }) {
    const map = useMapEvents({
        click(e) {
            setPosition([e.latlng.lat, e.latlng.lng]);
        },
    });

    // Center map on marker position when position changes
    useEffect(() => {
        if (position && position[0] && position[1]) {
            map.flyTo(position, map.getZoom());
        }
    }, [position, map]);

    return position ? <Marker position={position} /> : null;
}

// The fields that differ from the loaded trial. Only those are saved: an old trial may hold values the
// API no longer accepts (e.g. -1 visits), which must not stop saving another field.
function changedFields(original, current) {
    const changes = {};
    for (const [key, value] of Object.entries(current)) {
        const before = original?.[key];
        const same = value !== null && typeof value === 'object'
            ? JSON.stringify(value) === JSON.stringify(before)
            : String(value ?? '') === String(before ?? '');
        if (!same) changes[key] = value;
    }
    return changes;
}

export default function TrialEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const vanoCodes = useVanoCodes();
    const [trial, setTrial] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});
    const [mapPosition, setMapPosition] = useState(null);
    const [users, setUsers] = useState([]);
    const [hosts, setHosts] = useState([]);
    const [crops, setCrops] = useState([]);
    const [countries, setCountries] = useState([]);
    const [studyTypes, setStudyTypes] = useState([]);
    const [trialTypes, setTrialTypes] = useState([]);
    const [hostDialogOpen, setHostDialogOpen] = useState(false);
    // Why saving failed (e.g. a trial number that is taken), shown in the form
    const [saveError, setSaveError] = useState('');
    // The Year ↔ PO year warning only once the user changes the Year (not for how an old trial is)
    const [yearTouched, setYearTouched] = useState(false);
    // The trial number: read-only, except for an admin who chooses "Change" (confirmed when saving)
    const [isAdmin, setIsAdmin] = useState(false);
    const [trialnoUnlocked, setTrialnoUnlocked] = useState(false);
    const [trialnoChanges, setTrialnoChanges] = useState(null); // the changes awaiting confirmation
    const [closeChanges, setCloseChanges] = useState(null); // the changes that close the trial, awaiting confirmation
    useEffect(() => {
        getUserInfo().then((u) => setIsAdmin(Array.isArray(u?.roles) && u.roles.includes('admin')));
    }, []);
    const mapRef = useRef(null);
    const loadedMapPosition = useRef(null);
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : '';

    // Load users for FT dropdown
    useEffect(() => {
        axios.post(`${config.apiBaseUrl}/masterdata/users`, {}, {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
        }).then(res => {
            if (Array.isArray(res.data)) setUsers(res.data);
        }).catch(() => setUsers([]));
        api.post('/masterdata/hosts')
            .then(res => setHosts(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setHosts([]));
        api.post('/masterdata/crops')
            .then(res => setCrops(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setCrops([]));
        // The rest of the master data the wizard offers (archived ones too: the trial's current one stays shown)
        for (const [path, set] of [['/masterdata/countries', setCountries], ['/masterdata/studytypes', setStudyTypes], ['/masterdata/trialtype', setTrialTypes]]) {
            api.post(path)
                .then(res => set(Array.isArray(res?.data) ? res.data : []))
                .catch(() => set([]));
        }
    }, [token]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        axios.post(`${config.apiBaseUrl}/trials/${id}`, {}, {
            headers: {
                Authorization: token ? `Bearer ${token}` : undefined,
            },
        })
            .then(res => {
                setTrial(res.data);
                setForm(res.data);
                // Initialize map position if coordinates exist
                // Read whichever way the position is stored (utils/gps.js)
                const position = parseGps(res.data.PosGPSLatitude, res.data.PosGPSLongitude);
                if (position) {
                    const { lat, lng } = position;
                    if (!isNaN(lat) && !isNaN(lng)) {
                        // Only shown: the stored values stay as they are until the position is moved
                        loadedMapPosition.current = [lat, lng];
                        setMapPosition(loadedMapPosition.current);
                    }
                }
                setLoading(false);
            })
            .catch(err => {
                setError(err.response?.data || err.message);
                setLoading(false);
            });
    }, [id, token]);

    function handleChange(e) {
        const { name, value } = e.target;
        if (name === 'Year') setYearTouched(true);
        setForm(f => ({ ...f, [name]: value }));
    }

    function handleCheckbox(e) {
        const { name, checked } = e.target;
        setForm(f => ({ ...f, [name]: checked ? 1 : 0 }));
    }

    // Update coordinates from map position (not for the position loaded with the trial)
    useEffect(() => {
        if (mapPosition === loadedMapPosition.current) return;
        if (mapPosition && mapPosition.length === 2) {
            setForm(f => ({
                ...f,
                PosGPSLatitude: mapPosition[0].toFixed(5),
                PosGPSLongitude: mapPosition[1].toFixed(5)
            }));
        }
    }, [mapPosition]);

    // Back and Save return to where Edit was opened from: the trial (which keeps where its own Back
    // goes), or the list or dashboard Edit was opened from directly
    function goBack() {
        const from = location.state?.from;
        if (location.state?.via === 'view' || !from) {
            navigate(`/trials/${id}`, { replace: true, state: from ? { from } : undefined });
        } else {
            navigate(from, { replace: true });
        }
    }

    async function handleSubmit(e) {
        e.preventDefault();
        const changes = changedFields(trial, form);
        if (Object.keys(changes).length === 0) {
            goBack();
            return;
        }
        // A new trial number (admins only) is confirmed first
        if ('trialno' in changes) {
            setTrialnoChanges(changes);
            return;
        }
        await saveOrConfirmClose(changes);
    }

    // Closing the trial is confirmed before saving (reopening is not)
    async function saveOrConfirmClose(changes) {
        if (Number(changes.Closed) === 1) {
            setTrialnoChanges(null);
            setCloseChanges(changes);
            return;
        }
        await save(changes);
    }

    async function save(changes) {
        setTrialnoChanges(null);
        setCloseChanges(null);
        setSaving(true);
        setSaveError('');
        try {
            await axios.post(`${config.apiBaseUrl}/trials/${id}/update`, changes, {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined,
                },
            });
            // On success: show a success toast
            try {
                window.dispatchEvent(new CustomEvent('app:toast', { detail: { type: 'success', message: 'Changes saved' } }));
            } catch (e) { /* ignore */ }
            goBack();
        } catch (err) {
            const status = err.response?.status;
            setSaveError((status === 422 || status === 403) && err.response.data?.message
                ? err.response.data.message
                : `Could not save the trial${status ? ` (server error ${status})` : ''}. Please try again.`);
            // Emit a toast event for failure
            try {
                window.dispatchEvent(new CustomEvent('app:toast', { detail: { type: 'error', message: (err.response?.data?.message || err.message || 'Save failed') } }));
            } catch (e) {
                // ignore
            }
        } finally {
            setSaving(false);
        }
    }

    const isGeolocationSupported = typeof navigator !== 'undefined' && 'geolocation' in navigator;

    if (loading) return <div className="p-8 text-center text-gray-500">Loading trial...</div>;
    if (error) return (
        <div className="p-8 text-center text-red-600">
            <div className="mb-2 font-bold">Error loading trial</div>
            <pre className="bg-red-100 rounded p-2 text-xs text-left overflow-x-auto">{JSON.stringify(error, null, 2)}</pre>
            <button className="mt-4 px-4 py-2 rounded bg-emerald-600 text-white" onClick={() => navigate(-1)}>Go Back</button>
        </div>
    );
    if (!trial) return <div className="p-8 text-center text-gray-500">Trial not found.</div>;

    return (
        <div className="w-full max-w-screen-xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 md:p-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-emerald-700 dark:text-emerald-400 mb-6 sm:mb-8">Edit Trial</h1>
            <ArchivedDataBanner trial={trial} />
            {/* noValidate: an old trial may hold values outside the fields' limits (e.g. -1 visits), which
                must not block saving other fields; the API checks the fields that are sent */}
            <form onSubmit={handleSubmit} noValidate className="space-y-6 sm:space-y-8">
                {/* General Information Section */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-emerald-700 dark:text-emerald-400">General Information</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                        <div>
                            <div className="mb-1 flex items-center justify-between gap-2">
                                <label htmlFor="trial-trialno" className="block text-sm font-medium">Trial No</label>
                                {isAdmin && !trialnoUnlocked && (
                                    <button type="button" className="text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-300" onClick={() => setTrialnoUnlocked(true)}>
                                        Change
                                    </button>
                                )}
                            </div>
                            <input
                                id="trial-trialno"
                                type="text"
                                name="trialno"
                                maxLength={TRIAL_MAX_LENGTH.trialno}
                                value={form.trialno || ''}
                                onChange={handleChange}
                                readOnly={!trialnoUnlocked}
                                className={`w-full border rounded px-3 py-2 dark:border-gray-700 ${trialnoUnlocked ? 'dark:bg-gray-800' : 'bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-400'}`}
                            />
                            {!isAdmin && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Only an admin can change the trial number.</p>}
                        </div>
                        {/* The trial's own status, apart from the activities (ticking it there by mistake closed trials, #18) */}
                        <div>
                            <p className="block text-sm font-medium mb-1">Status</p>
                            <div className="flex items-center gap-2 py-2">
                                <input type="checkbox" name="Closed" id="Closed" checked={!!form.Closed} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                                <label htmlFor="Closed" className="text-sm font-medium">Closed</label>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">A closed trial is hidden from the overview and the dashboard. Closing is confirmed when saving.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Year</label>
                            <input type="number" name="Year" {...YEAR_NUMBER} value={form.Year || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                            {yearTouched && yearWarning(form.Year, trial.project?.contract) && (
                                <p role="status" data-testid="year-warning" className="mt-1 text-xs text-amber-700 dark:text-amber-300">{yearWarning(form.Year, trial.project?.contract)}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Soil Sample</label>
                            <input type="text" name="EarthSample" maxLength={TRIAL_MAX_LENGTH.EarthSample} value={form.EarthSample || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Location Target</label>
                            <input type="text" name="locationtarget" maxLength={TRIAL_MAX_LENGTH.locationtarget} value={form.locationtarget || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <p id="trial-host-label" className="block text-sm font-medium mb-1">Host<ArchivedBadge row={byId(hosts, form.host_id)} /></p>
                            <div aria-labelledby="trial-host-label">
                                <SearchSelect
                                    options={optionsWithCurrent(hosts, form.host_id).map(h => ({ value: h.id, label: optionLabel(h, h.name) }))}
                                    value={form.host_id ?? null}
                                    onChange={(opt) => setForm(f => ({ ...f, host_id: opt ? opt.value : null }))}
                                    placeholder="Select host..."
                                />
                            </div>
                            <button type="button" onClick={() => setHostDialogOpen(true)} className="mt-1 text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-300">
                                + New host
                            </button>
                        </div>
                        <div>
                            <p id="trial-crop-label" className="block text-sm font-medium mb-1">Crop<ArchivedBadge row={byId(crops, form.crop_id)} /></p>
                            <div aria-labelledby="trial-crop-label">
                                <SearchSelect
                                    options={optionsWithCurrent(crops, form.crop_id).map(c => ({ value: c.id, label: optionLabel(c, c.name + (c.description ? ` (${c.description})` : '')) }))}
                                    value={form.crop_id ?? null}
                                    onChange={(opt) => setForm(f => ({ ...f, crop_id: opt ? opt.value : null }))}
                                    placeholder="Select crop..."
                                />
                            </div>
                        </div>
                        <div>
                            <p id="trial-country-label" className="block text-sm font-medium mb-1">Country<ArchivedBadge row={byId(countries, form.country_id)} /></p>
                            <div aria-labelledby="trial-country-label">
                                <SearchSelect
                                    options={optionsWithCurrent(countries, form.country_id).map(c => ({ value: c.id, label: optionLabel(c, c.name) }))}
                                    value={form.country_id ?? null}
                                    onChange={(opt) => setForm(f => ({ ...f, country_id: opt ? opt.value : null }))}
                                    placeholder="Select country..."
                                />
                            </div>
                        </div>
                        <div>
                            <p id="trial-study-type-label" className="block text-sm font-medium mb-1">Study Type<ArchivedBadge row={byId(studyTypes, form.study_type_id)} /></p>
                            <div aria-labelledby="trial-study-type-label">
                                <SearchSelect
                                    options={optionsWithCurrent(studyTypes, form.study_type_id).map(t => ({ value: t.id, label: optionLabel(t, t.name) }))}
                                    value={form.study_type_id ?? null}
                                    onChange={(opt) => setForm(f => ({ ...f, study_type_id: opt ? opt.value : null }))}
                                    placeholder="Select study type..."
                                />
                            </div>
                        </div>
                        <div>
                            <p id="trial-trial-type-label" className="block text-sm font-medium mb-1">Trial Type<ArchivedBadge row={byId(trialTypes, form.trial_type_id)} /></p>
                            <div aria-labelledby="trial-trial-type-label">
                                <SearchSelect
                                    options={optionsWithCurrent(trialTypes, form.trial_type_id).map(t => ({ value: t.id, label: optionLabel(t, t.name) }))}
                                    value={form.trial_type_id ?? null}
                                    onChange={(opt) => setForm(f => ({ ...f, trial_type_id: opt ? opt.value : null }))}
                                    placeholder="Select trial type..."
                                />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="trial-testitem" className="block text-sm font-medium mb-1">Test Item</label>
                            <input type="text" id="trial-testitem" name="testitem" maxLength={TRIAL_MAX_LENGTH.testitem} value={form.testitem || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label htmlFor="trial-subcontractor" className="block text-sm font-medium mb-1">Subcontractor</label>
                            <input type="text" id="trial-subcontractor" name="subcontractor" maxLength={TRIAL_MAX_LENGTH.subcontractor} value={form.subcontractor || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        {/* The PI is chosen below; old trials may still carry it as free text (as in the wizard: only then shown) */}
                        {trial.pitxt ? (
                            <div>
                                <label htmlFor="trial-pitxt" className="block text-sm font-medium mb-1">PI (old text)</label>
                                <input type="text" id="trial-pitxt" name="pitxt" maxLength={TRIAL_MAX_LENGTH.pitxt} value={form.pitxt || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                            </div>
                        ) : null}
                        <div>
                            <label className="block text-sm font-medium mb-1">Local Reference</label>
                            <input type="text" name="LocalReference" maxLength={TRIAL_MAX_LENGTH.LocalReference} value={form.LocalReference || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Customer Contact</label>
                            <input type="text" name="CustomerContact" value={form.CustomerContact || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Trial Address</label>
                            <input type="text" name="TrialAddress" maxLength={TRIAL_MAX_LENGTH.TrialAddress} value={form.TrialAddress || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Field Phase Start</label>
                            <DatePicker
                                label="Field Phase Start"
                                startMonth={FIELD_PHASE_START_MONTH}
                                endMonth={FIELD_PHASE_END_MONTH}
                                selected={parseYMDToLocalDate(form.FieldFaseStart)}
                                onChange={date => setForm(f => ({ ...f, FieldFaseStart: formatDateToYMD(date) }))}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Field Phase End</label>
                            <DatePicker
                                label="Field Phase End"
                                startMonth={FIELD_PHASE_START_MONTH}
                                endMonth={FIELD_PHASE_END_MONTH}
                                minDate={parseYMDToLocalDate(form.FieldFaseStart)}
                                selected={parseYMDToLocalDate(form.FieldFaseEnd)}
                                onChange={date => setForm(f => ({ ...f, FieldFaseEnd: formatDateToYMD(date) }))}
                            />
                        </div>
                        <div>
                            <label htmlFor="trial-po" className="block text-sm font-medium mb-1">{CO_LABEL}</label>
                            {/* The CO is the project's contract number: set by the API, not edited here */}
                            <input id="trial-po" type="text" readOnly value={form.project?.contract?.co ?? form.PO ?? ''} className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-600 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-400" />
                            <p className="mt-1 text-xs text-gray-500">Follows the trial's project.</p>
                        </div>
                    </div>
                </div>

                {/* Trial Details Section */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-emerald-700 dark:text-emerald-400">Trial Specifications</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4 mb-5 sm:mb-6">
                        <div>
                            <label className="block text-sm font-medium mb-1">Treatment Count</label>
                            <input type="number" name="TreatmentCount" {...NON_NEGATIVE_NUMBER} value={form.TreatmentCount || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Replication Count</label>
                            <input type="number" name="ReplicationCount" {...NON_NEGATIVE_NUMBER} value={form.ReplicationCount || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Application Count</label>
                            <input type="number" name="ApplicationCount" {...NON_NEGATIVE_NUMBER} value={form.ApplicationCount || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Visit Count</label>
                            <input type="number" name="VisitCount" {...NON_NEGATIVE_NUMBER} value={form.VisitCount ?? ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                            <p className="mt-1 text-xs text-gray-500">Visit 00 plus this many visits</p>
                        </div>
                        {form.PlotSize ? (
                            <>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Plot Size L</label>
                                    <input type="number" name="PlotSizeL" {...NON_NEGATIVE_NUMBER} value={form.PlotSizeL || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Plot Size W</label>
                                    <input type="number" name="PlotSizeB" {...NON_NEGATIVE_NUMBER} value={form.PlotSizeB || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                                </div>
                            </>
                        ) : null}
                        <div>
                            <label className="block text-sm font-medium mb-1">Contract Amount</label>
                            <input type="number" name="Contract_Amount" {...NON_NEGATIVE_NUMBER} value={form.Contract_Amount || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label htmlFor="trial-studydirector" className="block text-sm font-medium mb-1">Study Director<ArchivedBadge row={byId(users, form.studydirector)} /></label>
                            <select id="trial-studydirector" name="studydirector" value={form.studydirector || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700">
                                <option value="">Select Study Director</option>
                                {optionsWithCurrent(users, form.studydirector).map(u => (
                                    <option key={u.id} value={u.id}>{optionLabel(u, `${u.name || u.email} ${u.initials ? `(${u.initials})` : ''}`)}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Field Technician<ArchivedBadge row={byId(users, form.FT)} /></label>
                            <select name="FT" value={form.FT || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700">
                                <option value="">Select Field Technician</option>
                                {optionsWithCurrent(users, form.FT).map(u => (
                                    <option key={u.id} value={u.id}>{optionLabel(u, `${u.name || u.email} ${u.initials ? `(${u.initials})` : ''}`)}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Principal Investigator<ArchivedBadge row={byId(users, form.PI)} /></label>
                            <select name="PI" value={form.PI || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700">
                                <option value="">Select Principal Investigator</option>
                                {optionsWithCurrent(users, form.PI).map(u => (
                                    <option key={u.id} value={u.id}>{optionLabel(u, `${u.name || u.email} ${u.initials ? `(${u.initials})` : ''}`)}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <h3 className="text-md sm:text-lg font-semibold mb-3 text-emerald-700 dark:text-emerald-400">Trial Status</h3>
                    <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
                        {/* Yes / No / To be decided */}
                        {TRI_STATE_FIELDS.map((field) => {
                            // The activity's samples beside it: only while it is Yes (else off and empty; a number set
                            // before is kept, as it is not sent unless changed)
                            const samples = SAMPLE_FIELDS.find((s) => s.flag === field);
                            return (
                                <div key={field} className="rounded p-2 bg-gray-50 dark:bg-gray-900">
                                    <TriStateSelect id={field} label={activityLabel(vanoCodes, field, field)} value={form[field]} onChange={(value) => setForm((f) => ({ ...f, [field]: value }))} />
                                    {samples && (
                                        <div className="mt-2">
                                            <label htmlFor={samples.field} className="block text-xs font-medium mb-1">{samples.label}</label>
                                            <input type="number" id={samples.field} name={samples.field} {...NON_NEGATIVE_NUMBER} disabled={!takesSamples(form, field)} value={shownSamples(form, samples.field) ?? ''} onChange={handleChange} className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:text-gray-400 dark:bg-gray-800 dark:border-gray-700" />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="Destruction" id="Destruction" checked={!!form.Destruction} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="Destruction" className="text-sm font-medium">{activityLabel(vanoCodes, 'Destruction', 'Destruction')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="TrialSetupARM" id="TrialSetupARM" checked={!!form.TrialSetupARM} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="TrialSetupARM" className="text-sm font-medium">{activityLabel(vanoCodes, 'TrialSetupARM', 'Trial Setup ARM')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="Borders_Sprayed" id="Borders_Sprayed" checked={!!form.Borders_Sprayed} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="Borders_Sprayed" className="text-sm font-medium">{activityLabel(vanoCodes, 'Borders_Sprayed', 'Borders Sprayed')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="StOut" id="StOut" checked={!!form.StOut} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="StOut" className="text-sm font-medium">{activityLabel(vanoCodes, 'StOut', 'Stacked Out')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="PegsUp" id="PegsUp" checked={!!form.PegsUp} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="PegsUp" className="text-sm font-medium">{activityLabel(vanoCodes, 'PegsUp', 'Pegs Up')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="PlotSize" id="PlotSize" checked={!!form.PlotSize} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="PlotSize" className="text-sm font-medium">{activityLabel(vanoCodes, 'PlotSize', 'Plot Size')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="WOA" id="WOA" checked={!!form.WOA} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="WOA" className="text-sm font-medium">{activityLabel(vanoCodes, 'WOA', 'WOA')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="ResAr" id="ResAr" checked={!!form.ResAr} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="ResAr" className="text-sm font-medium">{activityLabel(vanoCodes, 'ResAr', 'ResAr')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="FinalReport" id="FinalReport" checked={!!form.FinalReport} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="FinalReport" className="text-sm font-medium">{activityLabel(vanoCodes, 'FinalReport', 'Final Report')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="DraftReport" id="DraftReport" checked={!!form.DraftReport} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="DraftReport" className="text-sm font-medium">{activityLabel(vanoCodes, 'DraftReport', 'Draft Report')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="AdditionalWork" id="AdditionalWork" checked={!!form.AdditionalWork} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="AdditionalWork" className="text-sm font-medium">{activityLabel(vanoCodes, 'AdditionalWork', 'Additional Work')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="RecordCleansing" id="RecordCleansing" checked={!!form.RecordCleansing} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="RecordCleansing" className="text-sm font-medium">{activityLabel(vanoCodes, 'RecordCleansing', 'Record Cleansing')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="HLLWonly" id="HLLWonly" checked={!!form.HLLWonly} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="HLLWonly" className="text-sm font-medium">{activityLabel(vanoCodes, 'HLLWonly', 'HLLW only')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="EndFieldPhase" id="EndFieldPhase" checked={!!form.EndFieldPhase} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="EndFieldPhase" className="text-sm font-medium">{activityLabel(vanoCodes, 'EndFieldPhase', 'End Field Phase')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="ARMReady" id="ARMReady" checked={!!form.ARMReady} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="ARMReady" className="text-sm font-medium">{activityLabel(vanoCodes, 'ARMReady', 'ARM Ready')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="ReportProofRead" id="ReportProofRead" checked={!!form.ReportProofRead} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="ReportProofRead" className="text-sm font-medium">{activityLabel(vanoCodes, 'ReportProofRead', 'Report Proof Read')}</label>
                        </div>
                        <div className="flex items-center gap-2 rounded p-2 bg-gray-50 dark:bg-gray-900">
                            <input type="checkbox" name="TrialDone" id="TrialDone" checked={!!form.TrialDone} onChange={handleCheckbox} className="h-4 w-4 text-emerald-600" />
                            <label htmlFor="TrialDone" className="text-sm font-medium">{activityLabel(vanoCodes, 'TrialDone', 'Trial Done')}</label>
                        </div>
                    </div>
                </div>

                {/* Location Section */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-emerald-700 dark:text-emerald-400">Location</h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">GPS Latitude</label>
                            <input type="text" name="PosGPSLatitude" maxLength={TRIAL_MAX_LENGTH.PosGPSLatitude} value={form.PosGPSLatitude || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">GPS Longitude</label>
                            <input type="text" name="PosGPSLongitude" maxLength={TRIAL_MAX_LENGTH.PosGPSLongitude} value={form.PosGPSLongitude || ''} onChange={handleChange} className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700" />
                        </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden mb-4" style={{ height: '350px', maxHeight: '50vh' }}>
                        {typeof window !== 'undefined' && (
                            <MapContainer
                                center={mapPosition || [55.47280, 9.84374]}
                                zoom={13}
                                style={{ height: '100%', width: '100%' }}
                                ref={mapRef}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <LocationMarker position={mapPosition} setPosition={setMapPosition} />
                            </MapContainer>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                        <button
                            type="button"
                            className="w-full sm:w-auto px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                            onClick={() => {
                                if (typeof window !== 'undefined' && 'geolocation' in navigator) {
                                    navigator.geolocation.getCurrentPosition(
                                        pos => {
                                            const lat = pos.coords.latitude;
                                            const lng = pos.coords.longitude;
                                            setMapPosition([lat, lng]);
                                        },
                                        err => {
                                            alert('Could not get device location: ' + err.message);
                                        }
                                    );
                                } else {
                                    alert('Geolocation is not supported by your browser');
                                }
                            }}
                        >
                            Get Device Location
                        </button>
                        <button
                            type="button"
                            className="w-full sm:w-auto px-4 py-2 rounded bg-gray-600 hover:bg-gray-700 text-white font-semibold"
                            onClick={() => {
                                if (mapRef.current) {
                                    const map = mapRef.current;
                                    map.setView(mapPosition || [55.47280, 9.84374], 13);
                                }
                            }}
                        >
                            Reset Map View
                        </button>
                    </div>
                    <div className="text-xs text-gray-500 mt-2 text-center">
                        Click on the map to set the location pin, or use the "Get Device Location" button
                    </div>
                </div>

                {/* Notes Section */}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                    <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-emerald-700 dark:text-emerald-400">Notes</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-2">Note 1 (General instructions)</label>
                            <textarea
                                name="Noter1"
                                value={form.Noter1 || ''}
                                onChange={handleChange}
                                className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700 min-h-[80px] sm:min-h-[100px]"
                                placeholder="Enter general trial instructions here..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Note 2 (Additional information)</label>
                            <textarea
                                name="Noter2"
                                value={form.Noter2 || ''}
                                onChange={handleChange}
                                className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700 min-h-[80px] sm:min-h-[100px]"
                                placeholder="Enter additional trial information here..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2">Note 3 (Restrictions and warnings)</label>
                            <textarea
                                name="Noter3"
                                value={form.Noter3 || ''}
                                onChange={handleChange}
                                className="w-full border rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700 min-h-[80px] sm:min-h-[100px]"
                                placeholder="Enter restrictions and warnings here..."
                            />
                        </div>
                    </div>
                </div>

                {saveError && (
                    <div role="alert" className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                        {saveError}
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row justify-end items-center gap-3 sm:gap-4 mt-6 sm:mt-8">
                    <button
                        type="button"
                        className="w-full sm:w-auto px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                        onClick={goBack}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="w-full sm:w-auto px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
            <HostModal
                open={hostDialogOpen}
                onClose={() => setHostDialogOpen(false)}
                onSaved={(host) => {
                    if (!host) return;
                    setHosts(list => [...list, host]);
                    setForm(f => ({ ...f, host_id: host.id }));
                }}
            />
            {trialnoChanges && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={(e) => { if (e.target === e.currentTarget) setTrialnoChanges(null); }}>
                    <div role="dialog" aria-label="Change trial number" className="w-full max-w-md break-words rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900">
                        <h2 className="mb-2 text-xl font-bold text-amber-700 dark:text-amber-400">Change trial number?</h2>
                        <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">
                            From <span className="font-mono">{trial.trialno}</span> to <span className="font-mono">{trialnoChanges.trialno}</span>.
                        </p>
                        <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">Reports and files that mention {trial.trialno} are not changed.</p>
                        <div className="flex justify-end gap-2">
                            <button type="button" className="rounded bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200" onClick={() => setTrialnoChanges(null)}>
                                Cancel
                            </button>
                            <button type="button" className="rounded bg-amber-600 px-4 py-2 font-semibold text-white hover:bg-amber-700" onClick={() => saveOrConfirmClose(trialnoChanges)}>
                                Change trial number
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {closeChanges && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={(e) => { if (e.target === e.currentTarget) setCloseChanges(null); }}>
                    <div role="dialog" aria-label="Close trial" className="w-full max-w-md break-words rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900">
                        <h2 className="mb-2 text-xl font-bold text-amber-700 dark:text-amber-400">Close trial {closeChanges.trialno ?? trial.trialno}?</h2>
                        <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
                            A closed trial is hidden from the overview and the dashboard. It can be reopened here by unticking Closed.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button type="button" className="rounded bg-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200" onClick={() => setCloseChanges(null)}>
                                Cancel
                            </button>
                            <button type="button" className="rounded bg-amber-600 px-4 py-2 font-semibold text-white hover:bg-amber-700" onClick={() => save(closeChanges)}>
                                Close trial
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
